import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
/**
 * Displays text with highlighted search matches. When a search term is provided:
 * - Strips newlines for clean display with CSS line-clamp
 * - Intelligently truncates the prefix to show the first match
 * - Adds "... " ellipsis when text is truncated at the start
 * - Lets CSS line-clamp handle trailing ellipsis
 */
export const HighlightedText = React.memo(function HighlightedText({ text, searchTerm, contextBefore = 50 }) {
    const { displayText, parts } = useMemo(() => {
        // strip newlines from text
        const normalizedText = text.replace(/\n/g, ' ');
        if (!searchTerm || !searchTerm.trim()) {
            return { displayText: normalizedText, parts: null };
        }
        try {
            const terms = searchTerm.trim().split(/\s+/);
            const wordCharacter = '[\\p{L}\\p{M}\\p{N}_]';
            const termPatterns = terms
                .map((term, index) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                // Vespa treats only the actively typed final term as a prefix.
                (index < terms.length - 1 ? `(?!${wordCharacter})` : ''))
                .sort((a, b) => b.length - a.length);
            const regex = new RegExp(`(?<!${wordCharacter})(${termPatterns.join('|')})`, 'giu');
            // find the first match position
            const match = regex.exec(normalizedText);
            if (!match) {
                return { displayText: normalizedText, parts: null };
            }
            const matchIndex = match.index;
            // if match is near the start, don't truncate
            if (matchIndex <= contextBefore) {
                const splitParts = normalizedText.split(regex);
                return { displayText: normalizedText, parts: splitParts };
            }
            // truncate prefix and add ellipsis
            const startIndex = Math.max(0, matchIndex - contextBefore);
            const truncatedText = '... ' + normalizedText.slice(startIndex);
            const splitParts = truncatedText.split(regex);
            return { displayText: truncatedText, parts: splitParts };
        }
        catch {
            // if regex fails, return text as-is
            return { displayText: normalizedText, parts: null };
        }
    }, [text, searchTerm, contextBefore]);
    if (!parts) {
        return _jsx(_Fragment, { children: displayText });
    }
    // when using split with a capturing group, matched parts are at odd indices
    return (_jsx(_Fragment, { children: parts.map((part, index) => index % 2 === 1 ? (_jsx("mark", { className: "bg-super-soft text-inherit dark:text-super-primary", children: part }, index)) : (_jsx("span", { children: part }, index))) }));
});
//# sourceMappingURL=HighlightedText.js.map